/**
 * ProvenanceGraph (B4) — an artifact's causal chain as an EntityGraph.
 *
 * Fetches `GET /api/artifacts/:artifactId/timeline` (the same endpoint the
 * `TaskTimeline` feed uses) and renders the entries as a left→right causal
 * chain: each timeline entry (event / rule_run / agent_run / decision) is a
 * node, consecutive entries in time order are connected by edges. This is the
 * graph counterpart to the existing `<Timeline>` feed — additive, opt-in (a
 * page wires it behind a "Graph" toggle); the feed stays the primary view.
 *
 * Fetch mirrors `TaskTimeline`: the one sanctioned direct `fetch` against the
 * custom timeline endpoint, with `credentials: "include"` for the session
 * cookie. Errors degrade inline — never thrown.
 */
import * as React from "react";
import { useApiUrl } from "@refinedev/core";
import type { TimelineEntry, TimelineEntryKind } from "@aos/shared";

import { humanize } from "@/lib/format";
import { LoadingState, ErrorState } from "@/components/ui/states";
import {
  EntityGraph,
  type EntityGraphNode,
  type EntityGraphEdge,
} from "@/components/graph";

interface TimelineResponse {
  items: TimelineEntry[];
}

/**
 * Accent per timeline entry kind. BLACK-only accent (design-system §1): every
 * kind reads the same near-black; the kind is distinguished by the node's
 * glyph + subtitle, not by colour.
 */
const KIND_ACCENT = "#111111";
const KIND_COLOR: Record<TimelineEntryKind, string> = {
  event: KIND_ACCENT,
  rule_run: KIND_ACCENT,
  agent_run: KIND_ACCENT,
  decision: KIND_ACCENT,
};

function useArtifactTimeline(artifactId: string | null | undefined) {
  const apiUrl = useApiUrl();
  const [state, setState] = React.useState<{
    items: TimelineEntry[];
    loading: boolean;
    error: string | null;
  }>({ items: [], loading: Boolean(artifactId), error: null });

  React.useEffect(() => {
    if (!artifactId) {
      setState({ items: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    fetch(`${apiUrl}/artifacts/${encodeURIComponent(artifactId)}/timeline`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Timeline request failed (${res.status})`);
        }
        return (await res.json()) as TimelineResponse;
      })
      .then((body) => {
        if (cancelled) return;
        setState({
          items: Array.isArray(body.items) ? body.items : [],
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          items: [],
          loading: false,
          error:
            err instanceof Error ? err.message : "Failed to load timeline.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [apiUrl, artifactId]);

  return state;
}

export interface ProvenanceGraphProps {
  artifactId: string | null | undefined;
  height?: number | string;
}

/**
 * Renders the artifact's timeline as a left→right causal chain via EntityGraph.
 * Nodes = timeline entries (glyph/accent by kind, subtitle = status); edges =
 * consecutive entries in chronological order.
 */
export function ProvenanceGraph({ artifactId, height = "55vh" }: ProvenanceGraphProps) {
  const { items, loading, error } = useArtifactTimeline(artifactId);

  // Entries arrive chronologically; keep that order for the chain. Synthesize a
  // stable node id (the entry id may repeat across kinds) so edges resolve.
  const ordered = React.useMemo(
    () =>
      items.map((entry, i) => ({
        entry,
        nodeId: `${entry.kind}:${entry.id}:${i}`,
      })),
    [items],
  );

  const nodes = React.useMemo<EntityGraphNode[]>(
    () =>
      ordered.map(({ entry, nodeId }) => ({
        id: nodeId,
        title: entry.title || humanize(entry.kind),
        type: entry.kind,
        subtitle: `${humanize(entry.kind)}${entry.status ? ` · ${humanize(entry.status)}` : ""}`,
        tag:
          entry.ref?.event_type ??
          entry.ref?.rule_id ??
          entry.ref?.executor_type ??
          undefined,
        accent: KIND_COLOR[entry.kind] ?? "#111111",
      })),
    [ordered],
  );

  const edges = React.useMemo<EntityGraphEdge[]>(() => {
    const out: EntityGraphEdge[] = [];
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const from = ordered[i];
      const to = ordered[i + 1];
      if (!from || !to) continue;
      out.push({
        id: `prov-${i}`,
        from: from.nodeId,
        to: to.nodeId,
      });
    }
    return out;
  }, [ordered]);

  if (loading) {
    return <LoadingState label="Loading provenance…" rows={3} />;
  }
  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <EntityGraph
      nodes={nodes}
      edges={edges}
      layoutMode="layered"
      direction="LR"
      height={height}
      emptyMessage="No provenance recorded yet."
    />
  );
}

export default ProvenanceGraph;
